import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Chip,
  Container,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

const ChatAiInterface = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm your chat assistant. How can I help you today?",
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [predefinedResponses, setPredefinedResponses] = useState([
    "Tell me about yourself",
    "What services do you offer?",
    "How can I contact support?",
    "I need help with my account",
  ]);
  const [isSettingUp, setIsSettingUp] = useState(true);
  const [newResponse, setNewResponse] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (inputMessage.trim()) {
      const newUserMessage = {
        id: messages.length + 1,
        text: inputMessage,
        sender: "user",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, newUserMessage]);
      setInputMessage("");

      // Simulate bot response after a short delay
      setTimeout(() => {
        const botResponse = {
          id: messages.length + 2,
          text: "Thanks for your message! I'll get back to you soon.",
          sender: "bot",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botResponse]);
      }, 1000);
    }
  };

  const handlePredefinedResponseClick = (response) => {
    const newUserMessage = {
      id: messages.length + 1,
      text: response,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, newUserMessage]);

    // Simulate bot response after a short delay
    setTimeout(() => {
      const botResponse = {
        id: messages.length + 2,
        text: `You asked: "${response}". I'm here to help with that!`,
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botResponse]);
    }, 1000);
  };

  const handleAddResponse = () => {
    if (newResponse.trim() && !predefinedResponses.includes(newResponse)) {
      setPredefinedResponses((prev) => [...prev, newResponse]);
      setNewResponse("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  if (isSettingUp) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Card elevation={3}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              align="center"
              color="primary"
            >
              Chat Setup
            </Typography>
            <Typography variant="body1" paragraph align="center">
              Add some predefined responses to get started with your chat
              interface
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Predefined Responses
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {predefinedResponses.map((response, index) => (
                  <Chip
                    key={index}
                    label={response}
                    onDelete={() => {
                      setPredefinedResponses((prev) =>
                        prev.filter((_, i) => i !== index)
                      );
                    }}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Add new response"
                  value={newResponse}
                  onChange={(e) => setNewResponse(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddResponse()}
                />
                <Button
                  variant="contained"
                  onClick={handleAddResponse}
                  disabled={!newResponse.trim()}
                >
                  Add
                </Button>
              </Box>
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => setIsSettingUp(false)}
                disabled={predefinedResponses.length === 0}
                startIcon={<SettingsIcon />}
              >
                Start Chatting
              </Button>
              {predefinedResponses.length === 0 && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  Please add at least one predefined response
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <BotIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Chat Assistant
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => setIsSettingUp(true)}
            title="Edit predefined responses"
          >
            <SettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, overflow: "auto", p: 2, backgroundColor: "#f5f5f5" }}>
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ p: 2, backgroundColor: "transparent" }}>
            <List sx={{ width: "100%" }}>
              {messages.map((message) => (
                <React.Fragment key={message.id}>
                  <ListItem
                    sx={{
                      justifyContent:
                        message.sender === "user" ? "flex-end" : "flex-start",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    {message.sender === "bot" && (
                      <ListItemAvatar sx={{ minWidth: 40, mt: 0.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <BotIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                    )}
                    <Box
                      sx={{
                        maxWidth: "70%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems:
                          message.sender === "user" ? "flex-end" : "flex-start",
                      }}
                    >
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          backgroundColor:
                            message.sender === "user"
                              ? "primary.main"
                              : "background.paper",
                          color:
                            message.sender === "user"
                              ? "primary.contrastText"
                              : "text.primary",
                          borderRadius: 2,
                          ...(message.sender === "user" && {
                            borderTopRightRadius: 0,
                          }),
                          ...(message.sender === "bot" && {
                            borderTopLeftRadius: 0,
                          }),
                        }}
                      >
                        <Typography variant="body1">{message.text}</Typography>
                      </Paper>
                      <Typography
                        variant="caption"
                        sx={{ mt: 0.5, color: "text.secondary" }}
                      >
                        {formatTime(message.timestamp)}
                      </Typography>
                    </Box>
                    {message.sender === "user" && (
                      <ListItemAvatar sx={{ minWidth: 40, mt: 0.5, ml: 1 }}>
                        <Avatar
                          sx={{ bgcolor: "grey.400", width: 32, height: 32 }}
                        >
                          <PersonIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                    )}
                  </ListItem>
                </React.Fragment>
              ))}
              <div ref={messagesEndRef} />
            </List>
          </Paper>
        </Container>
      </Box>

      <Box
        sx={{
          p: 2,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="md">
          {/* Predefined responses chips */}
          <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {predefinedResponses.map((response, index) => (
              <Chip
                key={index}
                label={response}
                onClick={() => handlePredefinedResponseClick(response)}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))}
          </Box>

          {/* Message input */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              variant="outlined"
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim()}
              sx={{
                height: 56,
                width: 56,
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
                "&:disabled": {
                  backgroundColor: "grey.300",
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ChatAiInterface;
